import DOMPurify from 'dompurify';

/**
 * HTML 콘텐츠를 안전하게 정화하는 유틸리티 함수
 */

// 기본 허용 태그 및 속성 설정
const DEFAULT_CONFIG = {
  ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'span', 'p', 'br', 'div', 'ul', 'ol', 'li'],
  ALLOWED_ATTR: ['class', 'style'],
  KEEP_CONTENT: true,
  RETURN_DOM: false,
  RETURN_DOM_FRAGMENT: false,
  RETURN_DOM_IMPORT: false,
};

// 텍스트 전용 설정 (태그 제거, 텍스트만 유지)
const TEXT_ONLY_CONFIG = {
  ALLOWED_TAGS: [],
  ALLOWED_ATTR: [],
  KEEP_CONTENT: true,
};

// 기본 텍스트 포맷팅만 허용하는 설정
const BASIC_FORMAT_CONFIG = {
  ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'br'],
  ALLOWED_ATTR: [],
  KEEP_CONTENT: true,
};

/**
 * HTML 콘텐츠를 기본 설정으로 정화합니다
 * @param html - 정화할 HTML 문자열
 * @returns 정화된 HTML 문자열
 */
export const sanitizeHtml = (html: string): string => {
  return DOMPurify.sanitize(html, DEFAULT_CONFIG) as string;
};

/**
 * HTML 콘텐츠에서 모든 태그를 제거하고 텍스트만 반환합니다
 * @param html - 정화할 HTML 문자열
 * @returns 태그가 제거된 텍스트
 */
export const sanitizeToText = (html: string): string => {
  return DOMPurify.sanitize(html, TEXT_ONLY_CONFIG) as string;
};

/**
 * HTML 콘텐츠를 기본 포맷팅 태그만 허용하여 정화합니다
 * @param html - 정화할 HTML 문자열
 * @returns 기본 포맷팅만 허용된 HTML 문자열
 */
export const sanitizeBasicFormat = (html: string): string => {
  return DOMPurify.sanitize(html, BASIC_FORMAT_CONFIG) as string;
};

/**
 * 사용자 정의 설정으로 HTML 콘텐츠를 정화합니다
 * @param html - 정화할 HTML 문자열
 * @param config - DOMPurify 설정
 * @returns 정화된 HTML 문자열
 */
export const sanitizeWithConfig = (html: string, config: Record<string, unknown>): string => {
  return DOMPurify.sanitize(html, config) as string;
};

/**
 * HTML 시작 부분의 빈 태그들을 제거하는 함수
 *
 * @description
 * 첫 번째 텍스트 콘텐츠가 나오기 전까지의 빈 태그들을 제거합니다.
 * 이렇게 하면 설명이 "..."으로 시작하는 문제를 방지할 수 있습니다.
 *
 * @example
 * ```html
 * <!-- Before -->
 * <p></p><ul><li>텍스트</li></ul>
 *
 * <!-- After -->
 * <ul><li>텍스트</li></ul>
 * ```
 *
 * @param element - 정리할 DOM 요소
 */
const removeLeadingEmptyTags = (element: Element | DocumentFragment): void => {
  const children = Array.from(element.children);

  for (let i = 0; i < children.length; i++) {
    const child = children[i];
    const text = child.textContent?.trim() || '';

    if (text === '') {
      child.remove();
    } else {
      break;
    }
  }
};

/**
 * HTML에서 의미 없는 빈 태그들을 정리하는 함수
 *
 * @description
 * 다음 작업을 수행합니다:
 * 1. 끝부분의 연속된 빈 태그들을 제거
 * 2. 중간의 빈 <p> 태그는 줄바꿈 역할을 하므로 <p><br></p>로 변환
 * 3. 의미 있는 콘텐츠 사이의 빈 태그만 유지
 *
 * @param element - 정리할 DOM 요소
 */
const cleanupEmptyTags = (element: Element | DocumentFragment): void => {
  const children = Array.from(element.children);

  // 1. 끝에서부터 연속된 빈 태그 제거
  for (let i = children.length - 1; i >= 0; i--) {
    const child = children[i];
    const text = child.textContent?.trim() || '';

    if (text === '') {
      child.remove();
    } else {
      break; // 의미 있는 콘텐츠를 만나면 중단
    }
  }

  // 2. 중간의 빈 <p> 태그 처리 (줄바꿈 역할)
  const remainingChildren = Array.from(element.children);
  remainingChildren.forEach((child, index) => {
    if (child.tagName.toLowerCase() === 'p' && child.textContent?.trim() === '') {
      // 마지막 요소가 아니고, 다음에 의미 있는 콘텐츠가 있으면 <br> 추가
      const nextSibling = remainingChildren[index + 1];
      if (nextSibling && nextSibling.textContent?.trim()) {
        const br = document.createElement('br');
        child.appendChild(br);
      } else {
        // 그 외에는 제거
        child.remove();
      }
    }
  });

  // 3. 중첩된 요소들도 재귀적으로 정리
  Array.from(element.children).forEach((child) => {
    if (child.children.length > 0) {
      cleanupEmptyTags(child);
    }
  });
};

/**
 * 설명 텍스트용 HTML 정화 (자산 설명 등에 사용)
 *
 * @description
 * DOMPurify를 사용하여 HTML을 정화하고,
 * 다음 작업을 수행합니다:
 * - 링크(<a> 태그)에 target="_blank"와 rel="noopener noreferrer"를 추가
 * - 의미 없는 빈 태그들을 정리
 * - 중간의 빈 <p> 태그를 <p><br></p>로 변환하여 줄바꿈 효과 제공
 * - 시맨틱 태그(h1-h6, pre, code, blockquote, hr) 지원
 * - 전역 훅 대신 DOM 노드를 직접 조작하여 race condition 방지
 *
 * @param html - 정화할 HTML 문자열
 * @returns 정화된 HTML 문자열
 */
export const sanitizeDescription = (html: string): string => {
  const sanitizedNode = DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      'b',
      'i',
      'em',
      'strong',
      'span',
      'p',
      'br',
      'ul',
      'ol',
      'li',
      'a',
      'h1',
      'h2',
      'h3',
      'h4',
      'h5',
      'h6',
      'pre',
      'code',
      'blockquote',
      'hr',
      'div',
      'mark',
    ],
    ALLOWED_ATTR: ['class', 'href', 'target', 'rel'],
    KEEP_CONTENT: true,
    RETURN_DOM: true,
  });

  if (sanitizedNode instanceof DocumentFragment || sanitizedNode instanceof Element) {
    // 링크 속성 추가
    sanitizedNode.querySelectorAll('a').forEach((anchor) => {
      anchor.setAttribute('target', '_blank');
      anchor.setAttribute('rel', 'noopener noreferrer');
    });

    // 빈 태그 정리
    removeLeadingEmptyTags(sanitizedNode);
    cleanupEmptyTags(sanitizedNode);

    return sanitizedNode instanceof DocumentFragment
      ? Array.from(sanitizedNode.children)
          .map((child) => (child as Element).outerHTML)
          .join('')
      : sanitizedNode.innerHTML;
  }

  return '';
};

/**
 * HTML 문자열에 검색어 하이라이트를 적용합니다
 *
 * @param html - 하이라이트를 적용할 HTML 문자열
 * @param searchQuery - 검색어
 * @param className - 하이라이트 요소에 적용할 클래스명 (기본값: '')
 * @returns 하이라이트가 적용된 HTML 문자열
 */
export const highlightHtmlString = (html: string, searchQuery: string, className: string = ''): string => {
  if (!searchQuery || !html) return html;

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    const escapedQuery = searchQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`(${escapedQuery})`, 'gi');

    const highlightTextNodes = (node: Node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent || '';
        if (regex.test(text)) {
          const fragment = document.createDocumentFragment();
          text.split(regex).forEach((part, i) => {
            if (!part) return;
            if (i % 2 === 1) {
              const mark = document.createElement('mark');
              if (className) mark.className = className;
              mark.appendChild(document.createTextNode(part));
              fragment.appendChild(mark);
            } else {
              fragment.appendChild(document.createTextNode(part));
            }
          });
          node.parentNode?.replaceChild(fragment, node);
        }
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node as Element;
        if (
          element.tagName !== 'SCRIPT' &&
          element.tagName !== 'STYLE' &&
          element.tagName !== 'CODE' &&
          element.tagName !== 'PRE'
        ) {
          Array.from(node.childNodes).forEach(highlightTextNodes);
        }
      }
    };

    highlightTextNodes(doc.body);

    return doc.body.innerHTML;
  } catch (error) {
    console.warn('Failed to highlight HTML string:', error);
    return html;
  }
};
